import React, { useRef, useState } from 'react';
import { createPlaceholderUrl } from '../../../../utils/PlaceholderCreator';
import { API_BASE_URL } from '../../../../constants';



const CostumeSelector = ({ costumes, selectedCostume, onCostumeChange, selectedCharacter }) => {
  const scrollRef = useRef(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // --- Image URL Generation Logic ---
  const sanitizeFilename = (name) => {
    if (typeof name !== 'string') return '';
    return name.replace(/[\\/*?:"<>|]/g, '');
  };

  const getThumbnailUrl = (costume) => {
    if (!selectedCharacter || !costume) return createPlaceholderUrl(100, 100, '?');

    // [SYNC CHECK] Prevent generating URLs if data is desynchronized (e.g. Character changed but Costumes list is updating)
    if (costume.Id_Personaje !== selectedCharacter.Id_Personaje) {
      return createPlaceholderUrl(100, 100, '...');
    }

    try {
      const characterName = sanitizeFilename(selectedCharacter.name_en).replace(/ /g, '_');
      const reference = sanitizeFilename(costume.reference).replace(/ /g, '_');
      const costumeName = sanitizeFilename(costume.title_en).replace(/ /g, '_');

      // Standard format: Character_Ref_Costume.png
      const imageName = `${characterName}_${reference}_${costumeName}.png`;
      const originalPath = `/images/character/${imageName}`;

      // Using whitelisted width 64 (closest to 32px display size)
      return `${API_BASE_URL}/api/thumb?img=${originalPath}&width=64`;
    } catch (e) {
      return createPlaceholderUrl(100, 100, 'Err');
    }
  };

  // --- Draggable Scroll Logic ---
  const handleMouseDown = (e) => {
    setIsDown(true);
    setIsDragging(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };
  const handleMouseLeave = () => setIsDown(false);
  const handleMouseUp = () => {
    setIsDown(false);
    setTimeout(() => setIsDragging(false), 0);
  };
  const handleMouseMove = (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
    if (Math.abs(walk) > 5) setIsDragging(true);
  };

  const handleOutfitClick = (costume) => {
    if (!isDragging) {
      onCostumeChange(costume.card_id); // Assuming App.js expects generic ID change or object
    }
  };

  if (!costumes) return null;

  return (
    <div className="w-full max-w-[512px] mx-auto">


      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-2 transition-colors duration-300 border border-gray-100 dark:border-slate-700/50 ring-1 ring-transparent dark:ring-white/5">
        <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; } .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>

        <div
          ref={scrollRef}
          className="flex space-x-2 overflow-x-auto pb-0.5 hide-scrollbar cursor-grab active:cursor-grabbing select-none"
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          {costumes.map((costume) => {
            const isSelected = selectedCostume && selectedCostume.card_id === costume.card_id;
            const thumbUrl = getThumbnailUrl(costume);

            return (
              <button
                key={costume.card_id}
                onClick={() => handleOutfitClick(costume)}
                className={`
                                    flex items-center gap-3 pl-2 pr-4 py-2 text-xs sm:text-sm font-bold transition-all duration-300 rounded-xl relative overflow-hidden group flex-shrink-0 border
                                    ${isSelected
                    ? 'bg-gray-50 dark:bg-slate-700/50 border-[#00c541] text-gray-800 dark:text-white shadow-sm'
                    : 'bg-transparent border-transparent text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700/30'
                  }
                                `}
              >
                {/* Thumbnail Circle */}
                <div className={`
                                    w-8 h-8 rounded-full overflow-hidden border-2 transition-colors duration-300 flex-shrink-0 relative bg-gray-200
                                    ${isSelected ? 'border-[#00c541]' : 'border-gray-200 dark:border-slate-600 group-hover:border-gray-300 dark:group-hover:border-slate-500'}
                                `}>
                  <img
                    src={thumbUrl}
                    alt={costume.title_en}
                    className="w-full h-full object-cover object-top transform scale-150 translate-y-1"
                    draggable="false"
                    onError={(e) => { e.target.src = createPlaceholderUrl(100, 100, '?'); }}
                  />
                </div>

                <span className="relative z-10 pointer-events-none whitespace-nowrap">{costume.title_en}</span>

                {/* Active Indicator Dot */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#00c541] shadow-[0_0_5px_#00c541]"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CostumeSelector;
// Revisado: 2025-12-18 [I18n Scan]
